import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Shield, Sparkles, Zap, CheckCircle2 } from 'lucide-react';

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-brand-500/20 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent-500/15 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-400/10 rounded-full blur-3xl" />

      <div className="relative container-max section-padding pt-24 sm:pt-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm text-brand-200 mb-6"
            >
              <Sparkles className="w-4 h-4 text-accent-400" />
              <span>The Future of Behavioral Health Practice</span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-[1.08] tracking-tight mb-6">
              <span className="whitespace-nowrap">AI-Powered Care</span>
              <br />
              <span className="whitespace-nowrap bg-clip-text text-transparent bg-gradient-to-r from-accent-400 via-accent-300 to-brand-300">
                From Session
              </span>
              <br />
              <span className="whitespace-nowrap">To Revenue</span>
            </h1>

            <div className="max-w-2xl mb-8 space-y-4">

              <ul className="space-y-2 text-sm text-brand-200/90">
                {[
                  'Reduce documentation time from 2 hours to under 15 minutes',
                  'Submit claims with 1 click instead of juggling multiple logins',
                  'Track claims, payments, ERAs, and revenue live in one platform',
                  'Monitor associate payroll in real time without spreadsheets',
                  'Schedule appointments with zero clicks using voice',
                  'Add recurring revenue with managed Medicare APCM for PCP clinics',
                  'Eliminate operational overload with one intelligent AI system',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent-400 shrink-0 mt-0.5" />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-sm sm:text-base font-semibold bg-clip-text text-transparent bg-gradient-to-r from-accent-400 to-accent-300">
                Less Admin. Faster Revenue. Smarter Care.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/register"
                className="btn-primary !py-4 !px-8 !text-base group"
              >
                Start 7-Day Free Trial
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/25 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
              >
                <Play className="w-4 h-4 mr-2" />
                Book A Demo
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-brand-300/80">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-accent-400" />
                HIPAA Compliant
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-accent-400" />
                No Credit Card Required
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent-400" />
                Setup in Minutes
              </div>
            </div>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
            className="relative hidden lg:block"
          >
            {/* AI Dashboard Mock */}
            <div className="relative">
              {/* Main card */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-2 text-xs text-white/50 font-mono">Doctorite AI Session</span>
                </div>

                {/* Session UI */}
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-accent-400 uppercase tracking-wider">Live Session Guidance</span>
                      <span className="flex items-center gap-1.5 text-xs text-green-400">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        Active
                      </span>
                    </div>
                    <div className="text-sm text-white/80 leading-relaxed">
                      "Patient mentions increased anxiety at work. Consider exploring
                      workplace stressors and coping mechanisms. PHQ-9 score trending up."
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <div className="text-xs text-brand-300 mb-1">Emotion Detected</div>
                      <div className="text-white font-semibold text-sm">Anxious - Moderate</div>
                      <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: '65%' }}
                          transition={{ delay: 1.5, duration: 1 }}
                        />
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <div className="text-xs text-brand-300 mb-1">SOAP Note</div>
                      <div className="text-white font-semibold text-sm">Auto-Generating</div>
                      <div className="mt-2 flex gap-1">
                        {['S', 'O', 'A', 'P'].map((letter, i) => (
                          <motion.span
                            key={letter}
                            initial={{ opacity: 0.3 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1 + i * 0.3, duration: 0.4 }}
                            className="px-2 py-0.5 bg-brand-500/30 rounded text-xs text-brand-200 font-mono"
                          >
                            {letter}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent-500/10 rounded-xl p-3 border border-accent-500/20">
                    <div className="flex items-center gap-2 text-xs text-accent-400 font-semibold mb-1">
                      <Sparkles className="w-3.5 h-3.5" />
                      AI Suggestion
                    </div>
                    <div className="text-sm text-white/70">
                      Consider GAD-7 reassessment. Last score: 14 (moderate).
                      Recommend discussing CBT techniques for workplace anxiety.
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 bg-accent-500 text-white px-4 py-2 rounded-xl shadow-lg text-sm font-semibold"
              >
                70% Less Documentation Time
              </motion.div>

              <motion.div
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-4 bg-white text-brand-900 px-4 py-2 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2"
              >
                <Shield className="w-4 h-4 text-brand-600" />
                HIPAA Certified
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}
